/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useState } from 'react';
import { Paragraph, Title, LinkButton, Checkbox, InfiniteScroll, Button } from '@cambridgeassessment/cambridge-ui';
import {
  useStyles,
  StyledInputContainer,
  StyledHeaderRow,
  StyledCell,
  StyledRowContainer,
  StyledHeaderCell,
  StyledContainer,
  StyledCheckboxCell,
  StyledDownloadButtonsContainer,
} from './WorkDownloading.styles';
import { useDispatch, useSelector } from 'react-redux';
import * as thunks from '../../../../store/sections/thunks';
import { RootState } from '../../../../store';
import {
  Assessment,
  BusinessUnit,
  Centre,
  Component,
  Series,
  FileTypeName,
  fileTypes,
  QualificationMin,
  DISABLED_BULK_DOWNLOAD_FILE_TYPES,
  BulkFileDownloadData,
  BUSINESS_UNITS,
} from '@cambridgeassessment/lib-ee-commons';
import { ListRowPropsExtended } from '@cambridgeassessment/cambridge-ui/build/components/InfiniteScroll/InfiniteScroll';
import { getSectionDownloadZipLink } from '../../../../service/sections';
import { DownloadData } from '../../../../types/Sections';
import { downloadFromUrl } from '../../../../util/downloadHandler';
import { CircularProgress } from '@material-ui/core';

interface Props {
  businessUnitId: BusinessUnit['id'];
  seriesId?: Series['id'];
  centreId: Centre['id'];
  assessmentCode?: Assessment['code'];
  componentNumber?: Component['number'];
  qualificationId?: QualificationMin['id'];
  sectionId?: string;
  learnerNumber: string;
  seriesName?: Series['name'];
  filesTotal: number;
  loadMoreItems: () => Promise<void>;
  titleByBU: string;
}

const DownloadTable = ({
  businessUnitId,
  seriesId,
  centreId,
  assessmentCode,
  componentNumber,
  qualificationId,
  sectionId,
  learnerNumber,
  filesTotal,
  seriesName,
  loadMoreItems,
  titleByBU,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const { fileResults, isDownloadFilesLoading } = useSelector((state: RootState) => state.sections);
  const [selectedRowIndexes, setSelectedRowIndexes] = useState<number[]>([]);

  const retrieveFileFormat = (originalFilename: string | null | undefined): FileTypeName | undefined => {
    if (!originalFilename) {
      return FileTypeName.COMPRESSION;
    }

    const extension = originalFilename && originalFilename.slice(originalFilename.lastIndexOf('.')).toLowerCase();

    return fileTypes.find((type) => type.formats.find((format) => format === extension))?.name;
  };

  const isRequiredDataPresent = (): boolean => !!seriesId && !!assessmentCode && !!componentNumber && !!qualificationId;

  const isFileAllowedForDownloadBulk = (
    originalFilename: string | undefined | null,
    currSectionId: number | undefined,
  ): boolean => {
    if (!originalFilename && !!currSectionId) {
      return false;
    }

    const fileTypeName = retrieveFileFormat(originalFilename);

    if (!fileTypeName) {
      return false;
    }

    return !DISABLED_BULK_DOWNLOAD_FILE_TYPES.includes(fileTypeName);
  };

  const isSupportingMaterial = (file: DownloadData): boolean => file.name === 'Supporting material';

  const downloadZipFile = (fileCentreId: Centre['id'], fileLearnerNumber: string, learnerUniqueId: string) => {
    if (!isRequiredDataPresent()) {
      return;
    }

    const downloadUrl = getSectionDownloadZipLink(
      businessUnitId,
      fileCentreId,
      seriesId as number,
      assessmentCode as string,
      componentNumber as string,
      fileLearnerNumber,
      learnerUniqueId,
      seriesName,
    );

    downloadFromUrl(downloadUrl, undefined, '_self');
  };

  const viewFile = (_id: string, uniqId: string) => dispatch(thunks.openFile(_id, 'inline', uniqId));

  const isDownloadAllFilesAvailable = useCallback(
    () =>
      !isDownloadFilesLoading &&
      fileResults?.some((file) => isFileAllowedForDownloadBulk(file.file?.originalFileName, file.sectionId)),
    [isDownloadFilesLoading, fileResults, isFileAllowedForDownloadBulk],
  );

  const downloadSectionFile = (_id: string, uniqId: string, originalFileName?: string) => {
    dispatch(thunks.openFile(_id, 'attachment', uniqId, '_self', originalFileName));
  };

  const downloadComponentFile = (
    _id: string,
    uniqId: string,
    originalFileName?: string,
    isPartOfMultipleFiles?: boolean,
  ) => {
    dispatch(thunks.openFile(_id, 'attachment', uniqId, '_self', originalFileName, isPartOfMultipleFiles, true));
  };

  const classes = useStyles();

  const onDownloadClick = (file: DownloadData, uniqId: string): void => {
    const originalFileName = file.file.originalFileName ?? undefined;
    const passOriginalName =
      file.isPartOfMultipleFiles && businessUnitId === BUSINESS_UNITS.OCR.id ? originalFileName : undefined;

    if (isSupportingMaterial(file)) {
      downloadZipFile(file.centre.id, String(file.learnerNumber), file.learnerUniqueId);
    } else if (file.learnerNumber) {
      downloadSectionFile(file._id, uniqId, passOriginalName);
    } else {
      downloadComponentFile(file._id, uniqId, passOriginalName, file.isPartOfMultipleFiles);
    }
  };

  const onRowToggle = (index: number): void => {
    const itemIndex = selectedRowIndexes.indexOf(index);

    setSelectedRowIndexes((prevRowIndexes) =>
      itemIndex > -1 ? prevRowIndexes.filter((rowIndex) => rowIndex !== index) : [...prevRowIndexes, index],
    );
  };

  const onDownloadAllClick = (downloadDataAvailableLearnerNumber: DownloadData['learnerNumber']): void => {
    if (!isRequiredDataPresent()) {
      return;
    }

    const fileName = `${businessUnitId}_${seriesId}_${seriesName}_${assessmentCode}_${componentNumber}.zip`;

    dispatch(
      thunks.downloadAllFiles(
        businessUnitId,
        {
          qualificationId,
          seriesId,
          assessmentCode: assessmentCode as string,
          componentNumber: componentNumber as string,
          sectionId,
          centreId,
          learnerNumber,
          seriesName,
          isComponentDocument: String(!downloadDataAvailableLearnerNumber),
        },
        fileName,
      ),
    );
  };

  const onDownloadSelectedClick = (downloadDataAvailableLearnerNumber: DownloadData['learnerNumber']): void => {
    if (!fileResults || !seriesId || !assessmentCode || !componentNumber || !seriesName) {
      return;
    }

    const files: BulkFileDownloadData[] = [];

    for (const index of selectedRowIndexes) {
      const file = fileResults?.[index];

      if (!isFileAllowedForDownloadBulk(file.file.originalFileName, file.sectionId)) {
        return;
      }

      files.push({
        s3Key: file.file.s3Key,
        learnerNumber: file.learnerNumber,
        sectionId: file.sectionId,
        uniqueFileIdentifier: file.file.fileBusinessId.uniqueFileIdentifier,
        originalFilename: file.file.originalFileName ?? undefined,
        isMultipleSection: file.isPartOfMultipleFiles,
      });
    }

    const fileName = `${businessUnitId}_${seriesId}_${seriesName}_${assessmentCode}_${componentNumber}.zip`;
    const body = {
      params: {
        centreId,
        seriesId,
        seriesName,
        assessmentCode,
        componentNumber,
        isComponentDocument: String(!downloadDataAvailableLearnerNumber),
      },
      files,
    };

    dispatch(thunks.downloadSelectedFiles(businessUnitId, body, fileName));
  };

  const renderRow = ({ key, index, style }: ListRowPropsExtended, data: DownloadData[]): JSX.Element | null => {
    const file = data[index];

    return (
      <StyledRowContainer
        key={key}
        style={style}
        data-qa-id={`card-${file.centre.id}-${file.learnerNumber}-${file.name}`}
      >
        <StyledCheckboxCell maxWidth={'50px'}>
          <Checkbox
            onChange={() => onRowToggle(index)}
            checked={selectedRowIndexes.includes(index)}
            disabled={!isFileAllowedForDownloadBulk(file.file?.originalFileName, file.sectionId)}
            dataQaId={`checkbox-${file.centre.id}-${file.learnerNumber}-${file.name}`}
          />
        </StyledCheckboxCell>
        <StyledCell data-qa-id={`centre-${file.centre.id}-${file.learnerNumber}-${file.name}`}>
          {file.centre.id}
        </StyledCell>
        <StyledCell data-qa-id={`learnerNumber-${file.centre.id}-${file.learnerNumber}-${file.name}`}>
          {file.learnerNumber}
        </StyledCell>
        <StyledCell data-qa-id={`syllabus/specification-${file.centre.id}-${file.learnerNumber}-${file.name}`}>
          {file.assessment.code} - {file.component.name} - {file.name}
        </StyledCell>
        <StyledCell data-qa-id={`fileName-${file.centre.id}-${file.learnerNumber}-${file.name}`}>
          {retrieveFileFormat(file.file?.originalFileName)}
        </StyledCell>
        <StyledHeaderCell maxWidth={'76px'}>
          {retrieveFileFormat(file.file?.originalFileName) !== FileTypeName.COMPRESSION && (
            <LinkButton
              arrowType='down'
              endIcon={false}
              dataQaId={`view-${file.centre.id}-${file.learnerNumber}-${file.name}`}
              onClick={() => viewFile(file._id, file.file?.fileBusinessId.uniqueFileIdentifier ?? '')}
              passedClassName={classes.root}
            >
              View
            </LinkButton>
          )}
        </StyledHeaderCell>
        <StyledHeaderCell maxWidth={'76px'}>
          <LinkButton
            arrowType='down'
            endIcon={false}
            dataQaId={`download-${file.centre.id}-${file.learnerNumber}-${file.name}`}
            onClick={() => onDownloadClick(file, file.file?.fileBusinessId.uniqueFileIdentifier ?? '')}
            passedClassName={classes.root}
          >
            Download
          </LinkButton>
        </StyledHeaderCell>
      </StyledRowContainer>
    );
  };

  return (
    <>
      {fileResults && fileResults.length > 0 ? (
        <>
          <Title id='found'>{filesTotal} File(s) found</Title>
          <Paragraph data-qa-id='checkbox-explanation'>
            Download all files or select the appropriate checkboxes next to the files you want to download.
          </Paragraph>
          <Paragraph data-qa-id='supporting-material-explanation'>
            Video, presentation and supporting material cannot be downloaded in bulk.
          </Paragraph>
          <StyledDownloadButtonsContainer>
            <Button
              onClick={() => onDownloadAllClick(fileResults[0].learnerNumber)}
              disabled={!isDownloadAllFilesAvailable()}
              qaId='download-all-files'
            >
              Download all files
            </Button>
            {selectedRowIndexes.length > 0 && (
              <Button
                onClick={() => onDownloadSelectedClick(fileResults[0].learnerNumber)}
                disabled={isDownloadFilesLoading}
                qaId='download-selected-files'
              >
                Download {selectedRowIndexes.length} file(s)
              </Button>
            )}
            {isDownloadFilesLoading && <CircularProgress />}
          </StyledDownloadButtonsContainer>
          <StyledContainer>
            <StyledHeaderRow data-qa-id='report-details-table'>
              <StyledHeaderCell maxWidth={'50px'}></StyledHeaderCell>
              <StyledHeaderCell data-qa-id='centre-title'>Centre</StyledHeaderCell>
              <StyledHeaderCell data-qa-id='candidate-number-title'>Candidate number</StyledHeaderCell>
              <StyledHeaderCell data-qa-id='syllabus-component-title'>
                {titleByBU[0].toUpperCase() + titleByBU.slice(1)}, component, file
              </StyledHeaderCell>
              <StyledHeaderCell data-qa-id='file-format-title'>File format</StyledHeaderCell>
              <StyledHeaderCell maxWidth={'76px'}></StyledHeaderCell>
              <StyledHeaderCell maxWidth={'76px'}></StyledHeaderCell>
            </StyledHeaderRow>
            <InfiniteScroll
              rowRenderer={(props) => renderRow(props, fileResults)}
              loadMoreItems={loadMoreItems}
              data={fileResults}
              total={filesTotal}
              threshold={5}
            />
          </StyledContainer>
        </>
      ) : (
        <StyledInputContainer>
          <Title data-qa-id='no-results-title'>No results found</Title>
        </StyledInputContainer>
      )}
    </>
  );
};

export default DownloadTable;
